import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Banknote, Check, CreditCard, Landmark } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { fmtDate } from '../lib/format';
import { Alert, Badge, Button, Toggle, useToast } from './ui';

const eur = (c: number | null | undefined) => (c === null || c === undefined ? '—' : new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(c / 100));

/** Texto del método de pago guardado. */
export function paymentMethodLabel(pm: any): string {
  if (!pm) return 'Sin método de pago';
  if (pm.type === 'transfer') return 'Transferencia bancaria (contra factura)';
  if (pm.type === 'sepa_debit') return `Domiciliación SEPA ···· ${pm.last4 ?? ''}`;
  return `${(pm.brand ?? 'Tarjeta').replace(/^\w/, (c: string) => c.toUpperCase())} ···· ${pm.last4 ?? ''}`;
}

/**
 * Periodicidad (mensual / anual con descuento), método de pago y renovación.
 * `data` es la respuesta de GET /api/v1/billing. Gestiona también la vuelta desde Stripe (?setup=success&session=…).
 */
export function PlanAndPayment({ data, onChanged, compact = false }: { data: any; onChanged: () => void; compact?: boolean }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { reload } = useAuth();
  const [params, setParams] = useSearchParams();
  const handled = useRef(false);
  const sub = data.subscription;
  const terms = data.terms;
  const interval: 'MONTHLY' | 'ANNUAL' = sub?.nextBillingInterval ?? sub?.billingInterval ?? 'MONTHLY';
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['billing'] });
    await reload();
    onChanged();
  };

  // Vuelta desde Stripe tras guardar la tarjeta o el mandato SEPA
  useEffect(() => {
    if (handled.current) return;
    if (params.get('setup') === 'success' && params.get('session')) {
      handled.current = true;
      const confirm = (n: number): Promise<unknown> =>
        api.post('/api/v1/billing/payment-method/confirm', { sessionId: params.get('session') }).catch((e) => (n > 0 ? new Promise((r) => setTimeout(r, 1500)).then(() => confirm(n - 1)) : Promise.reject(e)));
      confirm(3)
        .then(() => toast.success('Método de pago guardado. No se ha cobrado nada.'))
        .catch(toast.error)
        .finally(() => (setParams({}, { replace: true }), refresh()));
    } else if (params.get('setup') === 'cancel') {
      handled.current = true;
      toast.info('No se ha guardado ningún método de pago.');
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const setup = useMutation({ mutationFn: () => api.post<{ url: string }>('/api/v1/billing/payment-method/setup'), onSuccess: (r) => (window.location.href = r.url), onError: toast.error });
  const transfer = useMutation({ mutationFn: () => api.post('/api/v1/billing/payment-method/transfer'), onSuccess: () => (toast.success('Pagarás por transferencia al recibir cada factura anual.'), refresh()), onError: toast.error });
  const save = async (patch: object) => {
    setBusy(true);
    try {
      const r = await api.put<{ message: string }>('/api/v1/billing/settings', patch);
      toast.success(r.message);
      await refresh();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  const reactivate = useMutation({ mutationFn: () => api.post('/api/v1/billing/reactivate'), onSuccess: () => (toast.success('Suscripción reactivada'), refresh()), onError: toast.error });

  const pm = sub?.paymentMethod;
  const isTrial = sub?.status === 'TRIAL';
  // Con precios con IVA incluido, el importe de la tarifa ya es el total
  const withTax = (c: number | null) => (c === null ? null : data.estimate?.taxIncluded ? c : Math.round(c * (1 + data.estimate.taxRate / 100)));
  const monthlyTotal = withTax(terms.monthlyCents);
  const annualTotal = withTax(terms.annualCents);

  const option = (id: 'MONTHLY' | 'ANNUAL') => {
    const selected = interval === id;
    return (
      <button
        type="button"
        role="radio"
        aria-checked={selected}
        disabled={busy}
        onClick={() => !selected && save({ billingInterval: id })}
        className={`relative flex-1 rounded-xl border p-4 text-left transition ${selected ? 'border-brand-600 bg-brand-50 ring-1 ring-brand-600' : 'border-slate-200 hover:border-brand-300'}`}
      >
        {id === 'ANNUAL' && terms.annualDiscountPct > 0 && <Badge color="green" className="absolute right-3 top-3">−{terms.annualDiscountPct}%</Badge>}
        <p className="font-semibold text-slate-900">{id === 'MONTHLY' ? 'Mensual' : 'Anual'}</p>
        <p className="mt-1 text-xl font-bold tabular-nums text-brand-950">{id === 'MONTHLY' ? eur(monthlyTotal) : eur(annualTotal)}</p>
        <p className="text-xs text-slate-500">{id === 'MONTHLY' ? 'al mes, IVA incl. · renovación automática' : `al año, IVA incl. (${eur(annualTotal === null ? null : Math.round(annualTotal / 12))}/mes)`}</p>
        {selected && <Check className="absolute bottom-3 right-3 h-5 w-5 text-brand-700" />}
      </button>
    );
  };

  return (
    <div className="space-y-5">
      {isTrial && sub?.trialEndsAt && (
        <Alert tone="info">
          Prueba gratuita hasta el <strong>{fmtDate(sub.trialEndsAt.slice(0, 10))}</strong>. Hoy no se cobra nada: el primer cargo se hará ese día{pm && pm.type !== 'transfer' ? ' automáticamente' : ''}. Puedes cancelar antes sin coste.
        </Alert>
      )}
      {sub?.complimentaryUntil && (
        <Alert tone="success" title="Suscripción de cortesía">
          {sub.complimentaryUntil >= '2099-01-01' ? 'Tienes la suscripción completa sin coste.' : `Tienes la suscripción completa sin coste hasta el ${fmtDate(sub.complimentaryUntil)}. Después se facturará con normalidad.`}
        </Alert>
      )}
      {sub?.status === 'CANCELLED' && (
        <Alert tone="warning" title="Suscripción cancelada">
          Tus registros se conservan y la plantilla puede seguir fichando. <Button size="sm" className="ml-2" onClick={() => reactivate.mutate()} loading={reactivate.isPending}>Reactivar</Button>
        </Alert>
      )}

      <div>
        <p className="mb-2 text-sm font-medium">Forma de pago del plan</p>
        <div role="radiogroup" aria-label="Periodicidad" className="flex flex-col gap-3 sm:flex-row">
          {option('MONTHLY')}
          {option('ANNUAL')}
        </div>
        <p className="mt-1.5 text-xs text-slate-500">
          Precio calculado con {data.usage.activeEmployees} trabajador(es) activo(s). Se cobra al empezar cada periodo.
          {sub?.nextBillingInterval && !isTrial ? ` El cambio a ${sub.nextBillingInterval === 'ANNUAL' ? 'anual' : 'mensual'} se aplicará en la próxima renovación.` : ''}
        </p>
      </div>

      <div>
        <p className="mb-2 text-sm font-medium">Método de pago</p>
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 p-4">
          <span className="flex items-center gap-2.5 text-sm">
            {pm?.type === 'transfer' ? <Landmark className="h-5 w-5 text-slate-500" /> : pm?.type === 'sepa_debit' ? <Banknote className="h-5 w-5 text-slate-500" /> : <CreditCard className="h-5 w-5 text-slate-500" />}
            <span className={pm ? 'font-medium' : 'text-slate-500'}>{paymentMethodLabel(pm)}</span>
          </span>
          <div className="flex flex-wrap gap-2">
            {terms.automaticAvailable && (
              <Button size="sm" icon={<CreditCard className="h-4 w-4" />} onClick={() => setup.mutate()} loading={setup.isPending}>
                {pm && pm.type !== 'transfer' ? 'Cambiar' : 'Añadir tarjeta o domiciliación SEPA'}
              </Button>
            )}
            {terms.transferAvailable && interval === 'ANNUAL' && pm?.type !== 'transfer' && (
              <Button size="sm" variant="secondary" onClick={() => transfer.mutate()} loading={transfer.isPending}>
                Pagar por transferencia
              </Button>
            )}
          </div>
        </div>
        <p className="mt-1.5 text-xs text-slate-500">
          {terms.automaticAvailable ? 'Tarjeta o domiciliación bancaria SEPA a través de Stripe: WulfTime no guarda los datos de tu tarjeta ni de tu cuenta.' : 'El cobro automático aún no está configurado en esta instalación: las facturas se pagan por transferencia, PayPal o tarjeta al recibirlas.'}
          {terms.transferAvailable ? ' La transferencia está disponible con el pago anual.' : ''}
        </p>
      </div>

      {!compact && sub && sub.status !== 'CANCELLED' && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 p-4">
          <Toggle checked={sub.autoRenew} disabled={busy} onChange={(v) => save({ autoRenew: v })} label="Renovación automática" description={sub.currentPeriodEnd && !isTrial ? `Periodo actual hasta el ${fmtDate(sub.currentPeriodEnd.slice(0, 10))}.` : 'Al desactivarla, la suscripción termina al final del periodo pagado.'} />
        </div>
      )}
    </div>
  );
}
